import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { assessmentDimensions } from '../../data/assessmentQuestions';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import SafeIcon from '../../common/SafeIcon';

const SCALE_LABELS = [
  "Helt uenig",
  "Delvis uenig",
  "Nøytral",
  "Delvis enig",
  "Helt enig"
];

export const AssessmentWizard = ({ onComplete }) => {
  const [currentDimIdx, setCurrentDimIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isFinished, setIsFinished] = useState(false);

  const currentDim = assessmentDimensions[currentDimIdx];

  const totalQuestions = useMemo(() => assessmentDimensions.reduce((acc, dim) => acc + dim.questions.length, 0), []);
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / totalQuestions) * 100;

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const nextCategory = () => {
    if (currentDimIdx < assessmentDimensions.length - 1) {
      setCurrentDimIdx(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsFinished(true);
    }
  };

  const prevCategory = () => {
    if (currentDimIdx > 0) {
      setCurrentDimIdx(prev => prev - 1);
    }
  };

  const allCategoryAnswered = currentDim.questions.every(q => answers[q.id] !== undefined);

  if (isFinished) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-12 max-w-md mx-auto"
      >
        <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
          <SafeIcon name="Check" className="text-4xl" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-4">Kartlegging fullført!</h2>
        <p className="text-slate-600 mb-8 leading-relaxed">
          Takk for din deltakelse. Dine svar er anonymisert og vil inngå i den samlede analysen av arbeidsmiljøet.
        </p>
        <Button onClick={() => onComplete(answers)} size="lg" className="w-full">
          Se resultater og analyse
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-20">
      {/* Progress Header */}
      <div className="sticky top-20 z-10 bg-slate-50/80 backdrop-blur-md py-4 -mx-4 px-4 rounded-b-2xl border-b border-slate-200/50">
        <div className="space-y-2">
          <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
            <span className="text-indigo-600">Kategori {currentDimIdx + 1} av {assessmentDimensions.length}</span>
            <span className="text-slate-500">{Math.round(progress)}% fullført</span>
          </div>
          <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
            <motion.div
              className="bg-indigo-600 h-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentDimIdx}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-8">
            <div className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 text-[10px] font-bold rounded-full uppercase tracking-widest mb-3">
              KARTLEGGING V4
            </div>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">{currentDim.name}</h2>
            <p className="text-slate-500 mt-2 text-lg">{currentDim.description}</p>
          </div>

          <div className="space-y-6">
            {currentDim.questions.map((question) => (
              <Card key={question.id} className={`${answers[question.id] ? 'border-indigo-200 ring-1 ring-indigo-50' : 'border-slate-200'} transition-all`}>
                <CardContent className="space-y-6">
                  <p className="text-xl font-semibold text-slate-800 leading-snug">{question.text}</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    {[1, 2, 3, 4, 5].map((val) => (
                      <button
                        key={val}
                        onClick={() => handleAnswer(question.id, val)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all group ${
                          answers[question.id] === val
                            ? 'border-indigo-600 bg-indigo-600 text-white shadow-lg shadow-indigo-100'
                            : 'border-slate-100 bg-slate-50 hover:border-slate-300 text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        <span className={`text-xl font-black ${answers[question.id] === val ? 'text-white' : 'text-slate-300 group-hover:text-slate-400'}`}>
                          {val}
                        </span>
                        <span className="text-[9px] uppercase tracking-tighter font-bold text-center leading-tight">
                          {SCALE_LABELS[val - 1]}
                        </span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 items-center">
        <Button
          variant="secondary"
          onClick={prevCategory}
          disabled={currentDimIdx === 0}
          icon={<SafeIcon name="ArrowLeft" />}
        >
          Forrige
        </Button>
        
        <div className="text-xs font-medium text-slate-400 italic">
          Alle spørsmål i kategorien må besvares
        </div>

        <Button
          variant="primary"
          onClick={nextCategory}
          disabled={!allCategoryAnswered}
          className="px-8"
          icon={<SafeIcon name={currentDimIdx === assessmentDimensions.length - 1 ? "Check" : "ArrowRight"} />}
        >
          {currentDimIdx === assessmentDimensions.length - 1 ? 'Fullfør' : 'Neste kategori'}
        </Button>
      </div>
    </div>
  );
};